import React, { useState } from 'react';
import { useApi, useRouteRefParams } from '@backstage/core-plugin-api';
import { workflowApiRef } from '../../../api';
import { useEntity } from '@backstage/plugin-catalog-react';
import { stringifyEntityRef } from '@backstage/catalog-model';
import { buildRouteRef } from '../../../routes';
import useAsyncRetry from 'react-use/esm/useAsyncRetry';
import { LinearProgress, Paper, Divider } from '@material-ui/core';
import { CodeSnippet } from '@backstage/core-components';

import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import { WarningPanel } from '@backstage/core-components';
import IconButton from '@material-ui/core/IconButton';
import RefreshIcon from '@material-ui/icons/Refresh';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles({
  root: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  logContainer: {
    flexGrow: 1,
    overflowY: 'auto',
    minHeight: 0, // Crucial for flex scrolling
  },
});

export const WorkflowRunLogs = ({
  projectId,
  location,
  status,
  steps,
}: {
  projectId: string;
  location: string;
  status?: string;
  steps?: any[];
}) => {
  const classes = useStyles();
  const api = useApi(workflowApiRef);
  const { entity } = useEntity();
  const { id } = useRouteRefParams(buildRouteRef);
  const [selectedTab, setSelectedTab] = useState<number>(0);

  const { value, loading, error, retry } = useAsyncRetry(async () => {
    // // START DUMMY DATA
    // const dummyLines = Array.from({ length: 1000 }, (_, i) => `[DUMMY LOG] Line ${i}: This is a generated log line to test the scrolling behavior and UI container. It is quite long to ensure it wraps or scrolls correctly.`);
    // return {
    //     steps: {
    //         0: dummyLines,
    //         1: dummyLines.map(l => l.replace('Line', 'Step 1 Line')),
    //     }
    // } as any;
    // // END DUMMY DATA

    return api.getWorkflowLogs({
      projectId,
      location,
      id,
      entityRef: stringifyEntityRef(entity),
    });
  }, [projectId, location, id, entity]);

  const handleRetry = () => {
    if (api.invalidateCache) {
      api.invalidateCache();
    }
    retry();
  };

  const handleChange = (_event: React.ChangeEvent<{}>, newValue: number) => {
    setSelectedTab(newValue);
  };

  if (loading) {
    return <LinearProgress />;
  }

  if (error) {
    return (
      <Paper className={classes.root}>
        <Box display="flex" justifyContent="space-between" alignItems="center" px={2} pt={2} pb={1}>
             <Typography variant="h6">Logs</Typography>
             <IconButton onClick={handleRetry} title="Refresh logs">
                <RefreshIcon />
             </IconButton>
        </Box>
        <Divider />
        <Box p={2}>
           <WarningPanel title="Error loading logs" message={error.message} />
        </Box>
      </Paper>
    );
  }

  if (!value) {
    const isRunning =
      status?.toLowerCase() === 'working' || status?.toLowerCase() === 'queued';
    return (
      <Paper className={classes.root}>
         <Box display="flex" justifyContent="space-between" alignItems="center" px={2} pt={2} pb={1}>
             <Typography variant="h6">Logs</Typography>
             <IconButton onClick={handleRetry} title="Refresh logs">
                <RefreshIcon />
             </IconButton>
        </Box>
        <Divider />
        <Box p={2}>
            <Typography variant="body1">
            {isRunning
                ? 'Logs are not available yet. Cloud Build saves the logs to the bucket after the build completes.'
                : 'No logs found for this build.'}
            </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper className={classes.root} elevation={0} variant="outlined">
       <Box display="flex" alignItems="center">
         <Box flexGrow={1}>
           <Tabs
             value={selectedTab}
             onChange={handleChange}
             indicatorColor="primary"
             textColor="primary"
             variant="scrollable"
             scrollButtons="auto"
           >
             {steps?.map((step, index) => (
                <Tab key={index} label={`Step ${index}: ${step.id || ''}`} />
             ))}
           </Tabs>
         </Box>
         <IconButton onClick={handleRetry} title="Refresh logs">
            <RefreshIcon />
         </IconButton>
       </Box>
       <Divider />
       <Box p={0} display="flex" flexDirection="column" flexGrow={1} minHeight={0}>
         <div className={classes.logContainer}>
           <CodeSnippet
             text={
               (value.steps[selectedTab] || []).join('\n') || 'No logs for this step.'
             }
             language="text"
             showCopyCodeButton
             customStyle={{ margin: 0, minHeight: '100%', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}
           />
         </div>
       </Box>
    </Paper>
  );
};
